import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FiActivity,
  FiCheckCircle,
  FiClipboard,
  FiClock,
  FiPlus,
  FiRefreshCcw,
  FiXCircle,
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import RequirementFilters from "./RequirementFilters";
import RequirementTable from "./RequirementTable";
import AddEditRequirement from "./AddEditRequirement";
import RequirementDrawer from "./RequirementDrawer";
import ChangeStatusDialog from "./ChangeStatusDialog";
import RequirementTasksDrawer from "./RequirementTasksDrawer";

import AddEditTask from "../../TaskManagement/AddEditTask";
import TaskDetailsDrawer from "../../TaskManagement/TaskDetailsDrawer";
import AddTaskComment from "../../TaskManagement/AddTaskComment";

import {
  EMPTY_REQUIREMENT_FILTERS,
  EMPTY_REQUIREMENT_PAGINATION,
  EMPTY_REQUIREMENT_SUMMARY,
  REQUIREMENT_TABS,
} from "./constants";

import "./RequirementManagement.css";


/* =========================================================
   STATUS ICONS
========================================================= */

const TAB_ICONS = {
  "": FiClipboard,
  Active: FiActivity,
  "In Progress": FiClock,
  Completed: FiCheckCircle,
  Closed: FiXCircle,
};


/* =========================================================
   COMPONENT
========================================================= */

const RequirementManagement = ({
  openCustomers,
}) => {
  const api = useMemo(
    () => new API(),
    []
  );

  const swal = useSwal();


  /* =======================================================
     DATA
  ======================================================= */

  const [
    requirements,
    setRequirements,
  ] = useState([]);


  const [
    summary,
    setSummary,
  ] = useState({
    ...EMPTY_REQUIREMENT_SUMMARY,
  });


  /* =======================================================
     FILTER / TAB
  ======================================================= */

  const [
    activeStatus,
    setActiveStatus,
  ] = useState("");


  const [
    filters,
    setFilters,
  ] = useState({
    ...EMPTY_REQUIREMENT_FILTERS,
  });


  const [
    appliedFilters,
    setAppliedFilters,
  ] = useState({
    ...EMPTY_REQUIREMENT_FILTERS,
  });


  /* =======================================================
     PAGINATION
  ======================================================= */

  const [
    pagination,
    setPagination,
  ] = useState({
    ...EMPTY_REQUIREMENT_PAGINATION,
  });


  /* =======================================================
     GENERAL STATE
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(false);


  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     REQUIREMENT FORM
  ======================================================= */

  const [
    formOpen,
    setFormOpen,
  ] = useState(false);


  const [
    editingRequirement,
    setEditingRequirement,
  ] = useState(null);


  /* =======================================================
     REQUIREMENT DRAWER
  ======================================================= */

  const [
    selectedRequirement,
    setSelectedRequirement,
  ] = useState(null);


  /* =======================================================
     STATUS DIALOG
  ======================================================= */

  const [
    statusRequirement,
    setStatusRequirement,
  ] = useState(null);


  /* =======================================================
     TASK SOURCE
  ======================================================= */

  const [
    taskRequirement,
    setTaskRequirement,
  ] = useState(null);


  /* =======================================================
     ASSIGN TASK
  ======================================================= */

  const [
    assignTaskOpen,
    setAssignTaskOpen,
  ] = useState(false);


  /* =======================================================
     REQUIREMENT TASKS
  ======================================================= */

  const [
    requirementTasksOpen,
    setRequirementTasksOpen,
  ] = useState(false);


  /* =======================================================
     TASK DETAILS
  ======================================================= */

  const [
    selectedTask,
    setSelectedTask,
  ] = useState(null);


  const [
    taskDetailsOpen,
    setTaskDetailsOpen,
  ] = useState(false);


  /* =======================================================
     TASK COMMENT
  ======================================================= */

  const [
    taskCommentOpen,
    setTaskCommentOpen,
  ] = useState(false);


  /* =======================================================
     FETCH REQUIREMENTS
  ======================================================= */

  const fetchRequirements =
    useCallback(
      async () => {
        try {
          setLoading(true);
          setError("");

          const payload = {
            page:
              pagination.page,

            limit:
              pagination.limit,

            status:
              activeStatus,

            search:
              appliedFilters
                .search
                ?.trim() ||
              "",

            requirementType:
              appliedFilters
                .requirementType ||
              "",

            priority:
              appliedFilters
                .priority ||
              "",

            dateFrom:
              appliedFilters
                .dateFrom ||
              "",

            dateTo:
              appliedFilters
                .dateTo ||
              "",

            sortOrder:
              "desc",
          };


          const response =
            await api.post(
              config.getRequirements,
              payload
            );


          if (
            response?.status ===
              "success" ||
            response?.success ===
              true
          ) {
            const records =
              Array.isArray(
                response?.data
              )
                ? response.data
                : [];


            setRequirements(
              records
            );


            setPagination(
              (previous) => ({
                ...previous,

                total:
                  Number(
                    response
                      ?.pagination
                      ?.total ||
                    0
                  ),

                page:
                  Number(
                    response
                      ?.pagination
                      ?.page ||
                    previous.page
                  ),

                limit:
                  Number(
                    response
                      ?.pagination
                      ?.limit ||
                    previous.limit
                  ),

                totalPages:
                  Math.max(
                    Number(
                      response
                        ?.pagination
                        ?.totalPages ||
                      1
                    ),
                    1
                  ),
              })
            );


            setSummary({
              ...EMPTY_REQUIREMENT_SUMMARY,

              ...(response
                ?.summary ||
                {}),
            });


            return;
          }


          throw new Error(
            response?.message ||
              "Unable to fetch customer requirements."
          );

        } catch (
          requestError
        ) {
          console.error(
            "Get requirements error:",
            requestError
          );


          setRequirements(
            []
          );


          setPagination(
            (previous) => ({
              ...previous,

              total:
                0,

              totalPages:
                1,
            })
          );


          setSummary({
            ...EMPTY_REQUIREMENT_SUMMARY,
          });


          setError(
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to fetch customer requirements."
          );

        } finally {
          setLoading(false);
        }
      },
      [
        api,
        activeStatus,
        appliedFilters,
        pagination.page,
        pagination.limit,
      ]
    );


  /* =======================================================
     LOAD
  ======================================================= */

  useEffect(
    () => {
      fetchRequirements();
    },
    [
      fetchRequirements,
    ]
  );


  /* =======================================================
     STATUS TAB
  ======================================================= */

  const handleTabChange = (
    status
  ) => {
    setActiveStatus(
      status
    );

    setPagination(
      (previous) => ({
        ...previous,

        page:
          1,
      })
    );
  };


  /* =======================================================
     SEARCH
  ======================================================= */

  const handleSearch =
    () => {
      setAppliedFilters({
        ...filters,
      });


      setPagination(
        (previous) => ({
          ...previous,

          page:
            1,
        })
      );
    };


  /* =======================================================
     RESET
  ======================================================= */

  const handleReset =
    () => {
      setFilters({
        ...EMPTY_REQUIREMENT_FILTERS,
      });


      setAppliedFilters({
        ...EMPTY_REQUIREMENT_FILTERS,
      });


      setActiveStatus(
        ""
      );


      setPagination(
        (previous) => ({
          ...previous,

          page:
            1,
        })
      );
    };


  /* =======================================================
     ADD
  ======================================================= */

  const handleAddRequirement =
    () => {
      setEditingRequirement(
        null
      );

      setFormOpen(
        true
      );
    };


  /* =======================================================
     EDIT
  ======================================================= */

  const handleEditRequirement = (
    requirement
  ) => {
    if (
      !requirement?._id
    ) {
      return;
    }

    setEditingRequirement(
      requirement
    );

    setFormOpen(
      true
    );
  };


  /* =======================================================
     VIEW
  ======================================================= */

  const handleViewRequirement = (
    requirement
  ) => {
    if (
      !requirement?._id
    ) {
      return;
    }

    setSelectedRequirement(
      requirement
    );
  };


  /* =======================================================
     CHANGE STATUS
  ======================================================= */

  const handleChangeStatus = (
    requirement
  ) => {
    if (
      !requirement?._id
    ) {
      return;
    }

    setStatusRequirement(
      requirement
    );
  };


  /* =======================================================
     DELETE REQUIREMENT
  ======================================================= */

  const handleDeleteRequirement =
    async (
      requirement
    ) => {
      if (
        !requirement?._id
      ) {
        return;
      }


      const customerName =
        requirement
          ?.customer
          ?.customerName ||
        requirement
          ?.customer
          ?.name ||
        "this customer";


      const confirmation =
        await swal.warning({
          icon:
            "warning",

          title:
            "Delete requirement?",

          html: `
            <div style="text-align:center">
              <p style="margin:0 0 8px">
                Are you sure you want to delete
                <strong>
                  ${requirement?.requirementType || "this requirement"}
                </strong>
                for
                <strong>
                  ${customerName}
                </strong>?
              </p>

              <small style="color:#667085">
                This requirement will be removed
                from the active list.
              </small>
            </div>
          `,

          showCancelButton:
            true,

          confirmButtonText:
            "Yes, Delete",

          cancelButtonText:
            "Cancel",

          confirmButtonColor:
            "#dc2626",
        });


      if (
        !confirmation?.isConfirmed
      ) {
        return;
      }


      try {
        /*
         * IMPORTANT:
         *
         * No request body here.
         *
         * ID is already present in URL.
         *
         * Do NOT use:
         *
         * api.delete(url, "")
         */

        const response =
          await api.delete(
            config
              .deleteRequirementById(
                requirement._id
              )
          );


        if (
          response?.status !==
            "success" &&
          response?.success !==
            true
        ) {
          throw new Error(
            response?.message ||
              "Unable to delete requirement."
          );
        }


        await swal.success({
          icon:
            "success",

          title:
            "Requirement Deleted",

          text:
            response?.message ||
            "Requirement deleted successfully.",

          timer:
            1400,

          showConfirmButton:
            false,
        });


        /*
         * Close requirement drawer
         * if same record is open.
         */

        if (
          String(
            selectedRequirement
              ?._id ||
            ""
          ) ===
          String(
            requirement._id
          )
        ) {
          setSelectedRequirement(
            null
          );
        }


        /*
         * If this was the last record on
         * the current page, move backward.
         */

        if (
          requirements.length ===
            1 &&
          pagination.page >
            1
        ) {
          setPagination(
            (previous) => ({
              ...previous,

              page:
                previous.page -
                1,
            })
          );

          return;
        }


        await fetchRequirements();

      } catch (
        requestError
      ) {
        console.error(
          "Delete requirement error:",
          requestError
        );


        await swal.error({
          icon:
            "error",

          title:
            "Delete Failed",

          text:
            requestError
              ?.response
              ?.data
              ?.message ||
            requestError
              ?.message ||
            "Unable to delete requirement.",
        });
      }
    };


  /* =======================================================
     ASSIGN TASK
  ======================================================= */

  const handleAssignTask = (
    requirement
  ) => {
    if (
      !requirement?._id
    ) {
      return;
    }


    setTaskRequirement(
      requirement
    );


    setRequirementTasksOpen(
      false
    );


    setAssignTaskOpen(
      true
    );
  };


  /* =======================================================
     VIEW REQUIREMENT TASKS
  ======================================================= */

  const handleViewRequirementTasks =
    (
      requirement
    ) => {
      if (
        !requirement?._id
      ) {
        return;
      }


      setTaskRequirement(
        requirement
      );


      setAssignTaskOpen(
        false
      );


      setRequirementTasksOpen(
        true
      );
    };


  /* =======================================================
     CLOSE ASSIGN TASK
  ======================================================= */

  const handleCloseAssignTask =
    () => {
      setAssignTaskOpen(
        false
      );


      setTaskRequirement(
        null
      );
    };


  /* =======================================================
     CLOSE REQUIREMENT TASKS
  ======================================================= */

  const handleCloseRequirementTasks =
    () => {
      setRequirementTasksOpen(
        false
      );


      setTaskRequirement(
        null
      );
    };


  /* =======================================================
     TASK CREATED
  ======================================================= */

  const handleTaskCreated =
    async () => {
      setAssignTaskOpen(
        false
      );


      setTaskRequirement(
        null
      );


      await fetchRequirements();
    };


  /* =======================================================
     FORM SUCCESS
  ======================================================= */

  const handleFormSuccess =
    async (
      savedRequirement
    ) => {
      setFormOpen(
        false
      );


      setEditingRequirement(
        null
      );


      /*
       * If drawer is currently open for
       * this record, update it immediately.
       */

      if (
        savedRequirement?._id
      ) {
        setSelectedRequirement(
          (current) => {
            if (
              String(
                current?._id ||
                ""
              ) ===
              String(
                savedRequirement._id
              )
            ) {
              return savedRequirement;
            }

            return current;
          }
        );
      }


      await fetchRequirements();
    };


  /* =======================================================
     STATUS SUCCESS
  ======================================================= */

  const handleStatusSuccess =
    async (
      updatedRequirement
    ) => {
      setStatusRequirement(
        null
      );


      if (
        updatedRequirement?._id
      ) {
        setSelectedRequirement(
          (current) => {
            if (
              String(
                current?._id ||
                ""
              ) ===
              String(
                updatedRequirement._id
              )
            ) {
              return updatedRequirement;
            }

            return current;
          }
        );
      }


      await fetchRequirements();
    };


  /* =======================================================
     PAGE CHANGE
  ======================================================= */

  const handlePageChange = (
    page
  ) => {
    const targetPage =
      Number(
        page
      );


    if (
      !Number.isInteger(
        targetPage
      ) ||
      targetPage <
        1 ||
      targetPage >
        pagination.totalPages ||
      targetPage ===
        pagination.page
    ) {
      return;
    }


    setPagination(
      (previous) => ({
        ...previous,

        page:
          targetPage,
      })
    );
  };


  /* =======================================================
     PAGE SIZE
  ======================================================= */

  const handleLimitChange = (
    event
  ) => {
    const nextLimit =
      Number(
        event
          ?.target
          ?.value
      );


    if (
      ![
        10,
        20,
        50,
        100,
      ].includes(
        nextLimit
      )
    ) {
      return;
    }


    setPagination(
      (previous) => ({
        ...previous,

        page:
          1,

        limit:
          nextLimit,
      })
    );
  };


  /* =======================================================
     VIEW TASK
  ======================================================= */

  const handleViewTask = (
    task
  ) => {
    if (
      !task?._id
    ) {
      return;
    }


    setSelectedTask(
      task
    );


    setRequirementTasksOpen(
      false
    );


    setTaskDetailsOpen(
      true
    );
  };


  /* =======================================================
     CLOSE TASK DETAILS
  ======================================================= */

  const handleCloseTaskDetails =
    () => {
      setTaskDetailsOpen(
        false
      );


      setSelectedTask(
        null
      );
    };


  /* =======================================================
     ADD TASK NOTE
  ======================================================= */

  const handleAddTaskNote = (
    task
  ) => {
    if (
      !task?._id
    ) {
      return;
    }


    setSelectedTask(
      task
    );


    setTaskDetailsOpen(
      false
    );


    setTaskCommentOpen(
      true
    );
  };


  /* =======================================================
     CLOSE TASK COMMENT
  ======================================================= */

  const handleCloseTaskComment =
    () => {
      setTaskCommentOpen(
        false
      );


      setSelectedTask(
        null
      );
    };


  /* =======================================================
     TASK COMMENT SUCCESS
  ======================================================= */

  const handleTaskCommentSuccess =
    async () => {
      setTaskCommentOpen(
        false
      );


      /*
       * Keep selectedTask so TaskDetailsDrawer
       * can reopen for the same task.
       */

      setTaskDetailsOpen(
        true
      );


      await fetchRequirements();
    };


  /* =======================================================
     OPEN CUSTOMER MODULE
  ======================================================= */

  const handleOpenCustomers =
    () => {
      setSelectedRequirement(
        null
      );


      if (
        typeof openCustomers ===
        "function"
      ) {
        openCustomers();
      }
    };


  /* =======================================================
     SOURCE CONTEXT
  ======================================================= */

  const taskSourceContext =
    taskRequirement
      ? {
          sourceType:
            "CUSTOMER_REQUIREMENT",

          sourceId:
            taskRequirement._id,

          sourceCode:
            taskRequirement
              .requirementCode ||
            taskRequirement
              .code ||
            "",

          sourceTitle:
            taskRequirement
              .requirementType ||
            "Customer Requirement",

          sourceDescription:
            taskRequirement
              .description ||
            "",

          sourceRoute:
            `/customer-requirements/${taskRequirement._id}`,

          customerId:
            taskRequirement
              ?.customer
              ?._id ||
            null,

          customerName:
            taskRequirement
              ?.customer
              ?.customerName ||
            taskRequirement
              ?.customer
              ?.name ||
            "",

          customerMobile:
            taskRequirement
              ?.customer
              ?.mobile ||
            taskRequirement
              ?.customer
              ?.phone ||
            "",

          customerEmail:
            taskRequirement
              ?.customer
              ?.email ||
            "",

          companyName:
            taskRequirement
              ?.customer
              ?.companyName ||
            "",
        }
      : null;


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="requirement-management-page"
    >
      {/* ===================================================
          HEADER
      =================================================== */}

      <div
        className="requirement-management-header"
      >
        <div>
          <span
            className="requirement-management-heading-label"
          >
            CUSTOMER SERVICE
          </span>


          <h3>
            Customer Requirements
          </h3>


          <p>
            Manage customer requests,
            priorities, follow-ups, tasks
            and requirement status.
          </p>
        </div>


        <div
          className="requirement-management-header-actions"
        >
          <button
            type="button"
            className="requirement-management-refresh-button"
            onClick={
              fetchRequirements
            }
            disabled={
              loading
            }
            title="Refresh requirements"
          >
            <FiRefreshCcw
              className={
                loading
                  ? "requirement-management-refresh-icon spinning"
                  : "requirement-management-refresh-icon"
              }
            />
          </button>


          <button
            type="button"
            className="requirement-management-add-button"
            onClick={
              handleAddRequirement
            }
          >
            <FiPlus />

            Add Requirement
          </button>
        </div>
      </div>


      {/* ===================================================
          STATUS TABS
      =================================================== */}

      <div
        className="requirement-status-tabs"
        role="tablist"
        aria-label="Requirement status"
      >
        {REQUIREMENT_TABS.map(
          (
            tab
          ) => {
            const Icon =
              TAB_ICONS[
                tab.key
              ] ||
              FiClipboard;


            const count =
              Number(
                summary[
                  tab.summaryKey
                ] ||
                0
              );


            const active =
              activeStatus ===
              tab.key;


            return (
              <button
                key={
                  tab.key ||
                  "all"
                }
                type="button"
                role="tab"
                aria-selected={
                  active
                }
                className={`requirement-status-tab ${
                  active
                    ? "active"
                    : ""
                }`}
                onClick={() =>
                  handleTabChange(
                    tab.key
                  )
                }
              >
                <Icon />


                <span>
                  {tab.label}
                </span>


                <strong>
                  {count}
                </strong>
              </button>
            );
          }
        )}
      </div>


      {/* ===================================================
          FILTERS
      =================================================== */}

      <RequirementFilters
        filters={
          filters
        }

        setFilters={
          setFilters
        }

        loading={
          loading
        }

        onSearch={
          handleSearch
        }

        onReset={
          handleReset
        }
      />


      {/* ===================================================
          ERROR
      =================================================== */}

      {error ? (
        <div
          className="requirement-management-error"
        >
          {error}
        </div>
      ) : null}


      {/* ===================================================
          TABLE
      =================================================== */}

      <RequirementTable
        requirements={
          requirements
        }

        loading={
          loading
        }

        pagination={
          pagination
        }

        onView={
          handleViewRequirement
        }

        onEdit={
          handleEditRequirement
        }

        onDelete={
          handleDeleteRequirement
        }

        onChangeStatus={
          handleChangeStatus
        }

        onPageChange={
          handlePageChange
        }

        onLimitChange={
          handleLimitChange
        }

        onAddRequirement={
          handleAddRequirement
        }

        onAssignTask={
          handleAssignTask
        }

        onViewTasks={
          handleViewRequirementTasks
        }
      />


      {/* ===================================================
          ADD / EDIT REQUIREMENT
      =================================================== */}

      <AddEditRequirement
        open={
          formOpen
        }

        requirement={
          editingRequirement
        }

        onClose={() => {
          setFormOpen(
            false
          );

          setEditingRequirement(
            null
          );
        }}

        onSuccess={
          handleFormSuccess
        }
      />


      {/* ===================================================
          REQUIREMENT DETAILS
      =================================================== */}

      <RequirementDrawer
        open={
          Boolean(
            selectedRequirement
          )
        }

        requirement={
          selectedRequirement
        }

        onClose={() =>
          setSelectedRequirement(
            null
          )
        }

        onEdit={(
          requirement
        ) => {
          setSelectedRequirement(
            null
          );

          handleEditRequirement(
            requirement
          );
        }}

        onChangeStatus={(
          requirement
        ) => {
          setSelectedRequirement(
            null
          );

          handleChangeStatus(
            requirement
          );
        }}

        onOpenCustomer={
          handleOpenCustomers
        }

        onAssignTask={(
          requirement
        ) => {
          setSelectedRequirement(
            null
          );

          handleAssignTask(
            requirement
          );
        }}

        onViewTasks={(
          requirement
        ) => {
          setSelectedRequirement(
            null
          );

          handleViewRequirementTasks(
            requirement
          );
        }}
      />


      {/* ===================================================
          ASSIGN TASK
      =================================================== */}

      <AddEditTask
        open={
          assignTaskOpen
        }

        task={
          null
        }

        sourceContext={
          taskSourceContext
        }

        onClose={
          handleCloseAssignTask
        }

        onSuccess={
          handleTaskCreated
        }
      />


      {/* ===================================================
          REQUIREMENT TASKS
      =================================================== */}

      <RequirementTasksDrawer
        open={
          requirementTasksOpen
        }

        requirement={
          taskRequirement
        }

        onClose={
          handleCloseRequirementTasks
        }

        onAssignTask={
          handleAssignTask
        }

        onViewTask={
          handleViewTask
        }
      />


      {/* ===================================================
          TASK DETAILS
      =================================================== */}

      <TaskDetailsDrawer
        open={
          taskDetailsOpen
        }

        task={
          selectedTask
        }

        taskId={
          selectedTask?._id
        }

        onClose={
          handleCloseTaskDetails
        }

        onEdit={(
          task
        ) => {
          setSelectedTask(
            task
          );

          setTaskDetailsOpen(
            false
          );
        }}

        onChangeStatus={(
          task
        ) => {
          setSelectedTask(
            task
          );
        }}

        onAddNote={
          handleAddTaskNote
        }

        onRefresh={
          fetchRequirements
        }
      />


      {/* ===================================================
          TASK COMMENT
      =================================================== */}

      <AddTaskComment
        open={
          taskCommentOpen
        }

        task={
          selectedTask
        }

        onClose={
          handleCloseTaskComment
        }

        onSuccess={
          handleTaskCommentSuccess
        }
      />


      {/* ===================================================
          CHANGE STATUS
      =================================================== */}

      <ChangeStatusDialog
        open={
          Boolean(
            statusRequirement
          )
        }

        requirement={
          statusRequirement
        }

        onClose={() =>
          setStatusRequirement(
            null
          )
        }

        onSuccess={
          handleStatusSuccess
        }
      />
    </div>
  );
};


export default RequirementManagement;